import { StatCard } from '@/components/custom/StatCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIndividualsApi } from '@/hooks/useIndividualsApi';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Flame, Loader2, Medal, Target, TrendingUp, Trophy } from 'lucide-react';

export default function IndividualProgress() {
  const individualsApi = useIndividualsApi();

  const { data: progressData, isLoading: isLoadingProgress } = useQuery({
    queryKey: ['individual-progress'],
    queryFn: async () => {
      const response = await individualsApi.getProgress();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const { data: prsData, isLoading: isLoadingPRs } = useQuery({
    queryKey: ['individual-prs'],
    queryFn: async () => {
      const response = await individualsApi.getPRs();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const isLoading = isLoadingProgress || isLoadingPRs;

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const summary = progressData?.summary;
  const weeklyTrend = progressData?.weekly_trend || [];
  const recentScores = progressData?.recent_scores || [];
  const prs = prsData?.prs || [];

  // Group PRs by track
  const prsByTrack = prs.reduce(
    (acc, pr) => {
      const track = pr.track_name || 'Other';
      if (!acc[track]) acc[track] = [];
      acc[track].push(pr);
      return acc;
    },
    {} as Record<
      string,
      Array<{
        workout_id: string;
        workout_title: string;
        track_name: string | null;
        scoring_type: string;
        best_result: string;
        best_value: number;
        achieved_at: string;
        attempts: number;
      }>
    >
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Simple bar chart using divs
  const maxWeekly = Math.max(...weeklyTrend.map((w) => w.count), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">My Progress</h1>
        <p className="mt-1 text-muted-foreground">
          Track your fitness journey and personal records.
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Scores"
          value={summary?.total_scores || 0}
          subtitle="All time"
          icon={Medal}
          delay={0}
        />
        <StatCard
          title="Unique Workouts"
          value={summary?.unique_workouts || 0}
          subtitle="Different WODs completed"
          icon={Target}
          delay={50}
        />
        <StatCard
          title="Current Streak"
          value={`${summary?.current_streak || 0} days`}
          subtitle="Keep it going!"
          icon={Flame}
          delay={100}
        />
        <StatCard
          title="Personal Records"
          value={prs.length}
          subtitle="Best scores tracked"
          icon={Trophy}
          delay={150}
        />
      </div>

      {/* Weekly Activity */}
      {weeklyTrend.length > 0 && (
        <Card className="animate-fade-in" style={{ animationDelay: '200ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Weekly Activity
            </CardTitle>
            <CardDescription>Workouts logged per week (last 12 weeks)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {weeklyTrend.map((week) => (
                <div key={week.week} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-muted-foreground">{week.count}</span>
                  <div
                    className="w-full bg-primary/80 rounded-t min-h-[4px]"
                    style={{ height: `${(week.count / maxWeekly) * 100}%` }}
                  />
                  <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                    {new Date(week.week).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Personal Records */}
      <Card className="animate-fade-in" style={{ animationDelay: '250ms' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Personal Records
          </CardTitle>
          <CardDescription>Your best score on each workout</CardDescription>
        </CardHeader>
        <CardContent>
          {prs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No personal records yet. Start logging scores!
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(prsByTrack).map(([trackName, trackPRs]) => (
                <div key={trackName}>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2">{trackName}</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Workout</TableHead>
                        <TableHead>Best Score</TableHead>
                        <TableHead className="hidden sm:table-cell">Achieved</TableHead>
                        <TableHead className="text-right">Attempts</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trackPRs.map((pr) => (
                        <TableRow key={pr.workout_id}>
                          <TableCell className="font-medium">{pr.workout_title}</TableCell>
                          <TableCell>
                            <span className="font-mono text-primary font-semibold">
                              {pr.best_result}
                            </span>
                            <Badge variant="outline" className="ml-2 text-xs">
                              {pr.scoring_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell text-muted-foreground">
                            {formatDate(pr.achieved_at)}
                          </TableCell>
                          <TableCell className="text-right">{pr.attempts}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="animate-fade-in" style={{ animationDelay: '300ms' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Recent Activity
          </CardTitle>
          <CardDescription>Your latest 20 logged scores</CardDescription>
        </CardHeader>
        <CardContent>
          {recentScores.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No scores logged yet.</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  window.location.href = '/individual/workouts';
                }}
              >
                Log Your First Score
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {recentScores.map((score, index) => (
                <div
                  key={score.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 animate-fade-in"
                  style={{ animationDelay: `${350 + index * 20}ms` }}
                >
                  <div>
                    <p className="font-medium">{score.workout_title}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{formatDate(score.created_at)}</span>
                      {score.track_name && (
                        <Badge variant="outline" className="text-xs">
                          {score.track_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-primary font-mono">{score.result}</p>
                    <div className="flex items-center gap-1 justify-end">
                      {score.rx && <span className="text-xs text-green-500 font-medium">Rx</span>}
                      <span className="text-xs text-muted-foreground">{score.scoring_type}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
